import type { TelemetryContext } from "@wishlink/applens-core";
import { parseInjectedObservability } from "./injectedObservability";
import { classifyStartType } from "./startType";
import { networkBand } from "./networkBand";
import { safely } from "./contracts";

import { createDocumentTimings } from "./documentTimings";

const LAUNCH_KEY = "wishlink.observability.launch-session.v1";
export interface BrowserSignals {
  document?: ReturnType<typeof createDocumentTimings>;
  context(): TelemetryContext;
  start(firstHome: boolean, now: number): ReturnType<typeof classifyStartType>;
  claimLaunch(firstHome: boolean, cold: boolean): number | undefined;
  visibility?(visible: boolean): void;
  listen(changed: () => void): () => void;
}
/** No listeners or random identifiers until activation. Native seed is never exported. */
export function createBrowserSignals() {
  const documentEpoch = safely(
    () => performance.timeOrigin || Date.now() - performance.now(),
    Date.now()
  );
  const native = safely(() => (window as any).ReactNativeWebView, undefined);
  const rawInjected = safely(() => native?.injectedObjectJson?.(), undefined);
  const injected = parseInjectedObservability(rawInjected, documentEpoch);
  let sessionId = injected.appSessionUUID;
  let sessionCreated = false;
  let lastVisibleAt: number | undefined;
  let hidden = false;
  const connection = () =>
    safely(() => (navigator as any).connection, undefined);
  const signals: BrowserSignals = {
    document: createDocumentTimings({
      timeOrigin: safely(() => performance.timeOrigin, NaN),
      now: () => performance.now(),
      navigation: () =>
        performance.getEntriesByType("navigation")[0] as
          | PerformanceNavigationTiming
          | undefined,
      injected: rawInjected,
    }),
    context() {
      if (!sessionCreated) {
        sessionCreated = true;
        if (!native)
          sessionId = safely(() => window.crypto.randomUUID(), undefined);
      }
      return {
        sessionId,
        networkBand: networkBand(connection()?.effectiveType),
      };
    },
    start(firstHome, now) {
      return classifyStartType({
        firstHome,
        homeStartedAt: now,
        lastVisibleAt,
        launchSurface: injected.launchSurface,
        navigationType: safely(
          () =>
            (
              performance.getEntriesByType(
                "navigation"
              )[0] as PerformanceNavigationTiming
            )?.type,
          undefined
        ),
      });
    },
    claimLaunch(firstHome, cold) {
      if (
        !firstHome ||
        !cold ||
        injected.launchSurface !== true ||
        !injected.appSessionUUID ||
        injected.launchStartEpochMs === undefined
      )
        return;
      return safely(() => {
        if (sessionStorage.getItem(LAUNCH_KEY) === injected.appSessionUUID)
          return;
        sessionStorage.setItem(LAUNCH_KEY, injected.appSessionUUID!);
        if (sessionStorage.getItem(LAUNCH_KEY) !== injected.appSessionUUID)
          return;
        return injected.launchStartEpochMs;
      }, undefined);
    },
    visibility(visible) {
      if (hidden && visible) lastVisibleAt = performance.now();
      hidden = !visible;
    },
    listen(changed) {
      hidden = document.visibilityState === "hidden";
      const visibility = () =>
        signals.visibility?.(document.visibilityState !== "hidden");
      const source = connection();
      document.addEventListener("visibilitychange", visibility);
      source?.addEventListener?.("change", changed);
      return () => {
        document.removeEventListener("visibilitychange", visibility);
        source?.removeEventListener?.("change", changed);
      };
    },
  };
  return {
    signals,
    injectedSeed: injected.seed,
    nativeIdentity: Object.freeze({
      ...(injected.nativeVersion !== undefined && {
        nativeVersion: injected.nativeVersion,
      }),
    }),
  };
}
